import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { DataGrid } from '../../datagrid/ResponsiveDataGrid';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  DesignServices as DesignServicesIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import * as manufacturingApi from '../../../../lib/api/manufacturing';

// Mock 설계 데이터 (다양한 상태)
const mockDesignData = [
  {
    id: 'design-001',
    designId: 'design-001',
    designNumber: 'DSN-20251015-0001',
    manuNo: 'DSN-20251015-0001',
    planId: 'plan-001',
    planNumber: 'MANU-20251001-0001',
    type: 'INTERNAL',
    typeLabel: '내부',
    status: 'APPROVED',
    statusLabel: '검수완료',
    designerName: '김설계 대리',
    designerId: 'user-001',
    inspectorName: '박검수 과장',
    inspectorId: 'user-002',
    products: [
      {
        productName: '메인보드',
        productUuid: 'product-001',
        spec: '30P',
        unit: 'EA',
        quantity: 1,
      },
    ],
    createdAt: '2025-10-10',
    updatedAt: '2025-10-15',
  },
  {
    id: 'design-002',
    designId: 'design-002',
    designNumber: 'DSN-20251013-0002',
    manuNo: 'DSN-20251013-0002',
    planId: 'plan-001',
    planNumber: 'MANU-20251001-0001',
    type: 'EXTERNAL',
    typeLabel: '외주',
    status: 'APPROVED',
    statusLabel: '검수완료',
    designerName: '이설계 과장',
    designerId: 'user-003',
    inspectorName: '최검수 대리',
    inspectorId: 'user-004',
    products: [
      {
        productName: 'PCB 보드',
        productUuid: 'product-002',
        spec: '50P',
        unit: 'EA',
        quantity: 2,
      },
    ],
    createdAt: '2025-10-11',
    updatedAt: '2025-10-13',
  },
  {
    id: 'design-003',
    designId: 'design-003',
    designNumber: 'DSN-20251020-0003',
    manuNo: 'DSN-20251020-0003',
    planId: 'plan-001',
    planNumber: 'MANU-20251001-0001',
    type: 'INTERNAL',
    typeLabel: '내부',
    status: 'IN_PROGRESS',
    statusLabel: '설계중',
    designerName: '박설계 사원',
    designerId: 'user-005',
    inspectorName: '-',
    inspectorId: null,
    products: [
      {
        productName: '컨트롤러',
        productUuid: 'product-003',
        spec: '10P',
        unit: 'EA',
        quantity: 3,
      },
    ],
    createdAt: '2025-10-20',
    updatedAt: '2025-10-20',
  },
  {
    id: 'design-004',
    designId: 'design-004',
    designNumber: 'DSN-20251022-0004',
    manuNo: 'DSN-20251022-0004',
    planId: 'plan-001',
    planNumber: 'MANU-20251001-0001',
    type: 'INTERNAL',
    typeLabel: '내부',
    status: 'REVIEW',
    statusLabel: '검토중',
    designerName: '정설계 대리',
    designerId: 'user-006',
    inspectorName: '-',
    inspectorId: null,
    products: [
      {
        productName: '센서 모듈',
        productUuid: 'product-004',
        spec: '20P',
        unit: 'EA',
        quantity: 5,
      },
    ],
    createdAt: '2025-10-22',
    updatedAt: '2025-10-22',
  },
];

/**
 * 설계 선택 모달
 * - planId로 해당 생산계획의 설계만 조회
 * - statusFilter로 특정 상태만 필터링 (기본: APPROVED - 검수완료)
 */
const DesignSelectModal = ({ 
  open, 
  onClose, 
  onSelect, 
  planId = null,
  statusFilter = 'APPROVED',  // 검수완료된 것만 기본
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [isMockData, setIsMockData] = useState(false);

  // 상태 라벨 매핑
  const mapDesignStatus = (status) => {
    const statusMap = {
      'PENDING': '대기',
      'IN_PROGRESS': '설계중',
      'COMPLETED': '설계완료',
      'REVIEW': '검수중',
      'APPROVED': '검수완료',
      'REJECTED': '반려',
      'CANCELLED': '취소',
    };
    return statusMap[status] || status || '-';
  };

  const mapDesignType = (type) => {
    return type === 'EXTERNAL' ? '외주' : '내부';
  };

  // Mock 데이터 로드
  const loadMockData = useCallback(() => {
    console.log('Mock 설계 데이터 사용');
    let filtered = mockDesignData;
    
    // planId로 필터 (plan-001은 항상 포함 - 테스트용)
    if (planId) {
      filtered = filtered.filter(d => d.planId === planId || d.planId === 'plan-001');
    }
    
    // 상태로 필터 (빈 문자열이면 모든 상태 허용)
    if (statusFilter && statusFilter !== '') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }
    
    setRows(filtered);
    setIsMockData(true);
  }, [planId, statusFilter]);

  // API 데이터 로드
  const fetchDesignData = useCallback(async () => {
    if (!open) return;
    
    setLoading(true);
    setError(null);

    try {
      console.log('설계 목록 API 호출 - planId:', planId, 'status:', statusFilter);
      
      const params = {
        status: statusFilter,
        page: 1,
        size: 100,
      };
      
      let response;
      if (planId) {
        // planId가 있으면 해당 계획의 설계만 조회
        response = await manufacturingApi.getDesignsByPlan(planId, { status: statusFilter });
      } else {
        response = await manufacturingApi.getDesigns(params);
      }
      
      console.log('설계 API 응답:', response.data);

      if (response.data.success && response.data.data) {
        const designs = response.data.data.content || response.data.data || [];
        
        const formattedDesigns = designs.map((item) => {
          // products 배열 구성 - API에서 단일 제품으로 오는 경우 처리
          let products = item.products || [];
          if (products.length === 0 && item.productName) {
            products = [{
              productName: item.productName,
              productId: item.productId,
              spec: item.cavity || '-',
              unit: 'EA',
              quantity: 1,
            }];
          }
          
          return {
            id: item.designId,
            designId: item.designId,
            designNumber: item.designNumber || item.manuNo,
            manuNo: item.designNumber || item.manuNo,
            planId: item.planId,
            planNumber: item.planNumber || item.planNo,
            // 제품 정보 (단일 필드)
            productId: item.productId,
            productName: item.productName || '',
            cavity: item.cavity || '',
            // 구분/상태
            type: item.designType || item.type,
            typeLabel: mapDesignType(item.designType || item.type),
            status: item.status,
            statusLabel: mapDesignStatus(item.status),
            // 담당자
            designerName: item.designerName || item.designer || '-',
            designerId: item.designerId,
            inspectorName: item.inspectorName || '-',
            inspectorId: item.inspectorId,
            // 제품 배열
            products: products,
            // 날짜
            createdAt: item.createdAt ? item.createdAt.split(' ')[0] : '-',
            updatedAt: item.updatedAt ? item.updatedAt.split(' ')[0] : '-',
          };
        });
        
        setRows(formattedDesigns);
        setIsMockData(false);
      } else {
        loadMockData();
      }
    } catch (err) {
      console.error('설계 목록 조회 에러:', err);
      loadMockData();
      setError('서버 연결 실패 - Mock 데이터를 표시합니다.');
    } finally {
      setLoading(false);
    }
  }, [open, planId, statusFilter, loadMockData]);

  // 모달 열릴 때 데이터 로드
  useEffect(() => {
    if (open) {
      fetchDesignData();
    }
  }, [open, fetchDesignData]);

  // 검색 필터링
  const filteredRows = useMemo(() => {
    if (!searchText) return rows;
    
    const lowercasedSearch = searchText.toLowerCase();
    return rows.filter(
      (row) =>
        (row.designNumber || '').toLowerCase().includes(lowercasedSearch) ||
        (row.manuNo || '').toLowerCase().includes(lowercasedSearch) ||
        (row.planNumber || '').toLowerCase().includes(lowercasedSearch) ||
        (row.designerName || '').toLowerCase().includes(lowercasedSearch) ||
        (row.products || []).some(p => (p.productName || '').toLowerCase().includes(lowercasedSearch))
    );
  }, [searchText, rows]);

  const columns = [
    {
      field: 'designNumber',
      headerName: '설계번호',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary">
          {params.value || params.row.manuNo}
        </Typography>
      ),
    },
    {
      field: 'planNumber',
      headerName: '생산계획',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip 
          label={params.value || '-'}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}
        />
      ),
    },
    {
      field: 'products',
      headerName: '제품 정보',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const products = params.value || [];
        const productName = params.row.productName;
        const cavity = params.row.cavity;
        
        // products 배열이 있는 경우
        if (products.length > 0) {
          if (products.length === 1) {
            return `${products[0].productName} (${products[0].spec || '-'})`;
          }
          return `${products[0].productName} 외 ${products.length - 1}건`;
        }
        
        // 단일 제품 필드인 경우
        if (productName) {
          return `${productName}${cavity ? ` (${cavity})` : ''}`;
        }
        
        return '-';
      },
    },
    {
      field: 'typeLabel',
      headerName: '구분',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip 
          label={params.value}
          size="small"
          color={params.row.type === 'INTERNAL' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'designerName',
      headerName: '설계 담당자',
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'statusLabel',
      headerName: '상태',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const getStatusColor = () => {
          switch (params.row.status) {
            case 'APPROVED': return 'success';
            case 'REVIEW': return 'warning';
            case 'IN_PROGRESS': return 'info';
            case 'REJECTED': return 'error';
            case 'CANCELLED': return 'default';
            default: return 'default';
          }
        };
        return (
          <Chip
            label={params.value}
            size="small"
            color={getStatusColor()}
            variant="filled"
          />
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: '변경일',
      width: 110,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  const handleRowClick = (params) => {
    setSelectedRow(params.row);
  };

  const handleConfirm = () => {
    if (selectedRow) {
      onSelect(selectedRow);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchText('');
    setSelectedRow(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DesignServicesIcon color="primary" />
            <Typography variant="h6">설계 선택</Typography>
            {statusFilter && statusFilter !== '' && (
              <Chip 
                label={`${statusFilter === 'APPROVED' ? '검수완료' : statusFilter}만`} 
                size="small" 
                color="success" 
                variant="outlined"
              />
            )}
            {isMockData && (
              <Chip 
                label="Mock" 
                size="small" 
                color="warning" 
                variant="filled"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={fetchDesignData} size="small" title="새로고침">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* 에러/알림 */}
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 검색 영역 */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="설계번호, 생산계획, 제품명, 담당자로 검색하세요..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* 선택된 설계 표시 */}
        {selectedRow && (
          <Paper
            elevation={2}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: '#e3f2fd',
              border: '2px solid #2196f3',
            }}
          >
            <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
              ✓ 선택된 설계
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
              <Typography variant="body2">
                <strong>설계번호:</strong> {selectedRow.designNumber || selectedRow.manuNo}
              </Typography>
              <Typography variant="body2">
                <strong>생산계획:</strong> {selectedRow.planNumber || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>담당자:</strong> {selectedRow.designerName}
              </Typography>
            </Box>
            {(selectedRow.products && selectedRow.products.length > 0) ? (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">제품:</Typography>
                {selectedRow.products.map((p, idx) => (
                  <Chip 
                    key={idx} 
                    label={`${p.productName} (${p.spec || '-'})`} 
                    size="small" 
                    variant="outlined"
                  />
                ))}
              </Box>
            ) : selectedRow.productName ? (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">제품:</Typography>
                <Chip 
                  label={`${selectedRow.productName}${selectedRow.cavity ? ` (${selectedRow.cavity})` : ''}`} 
                  size="small" 
                  variant="outlined"
                />
              </Box>
            ) : null}
          </Paper>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <DesignServicesIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              검수완료된 설계가 없습니다.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              설계 탭에서 설계를 완료하고 검수를 받아주세요.
            </Typography>
          </Paper>
        ) : (
          /* 데이터 그리드 */
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              onRowClick={handleRowClick}
              getRowId={(row) => row.id || row.designId}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: '#e3f2fd !important',
                },
                '& .MuiDataGrid-row.Mui-selected:hover': {
                  backgroundColor: '#bbdefb !important',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} size="large">
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedRow}
          size="large"
          startIcon={<DesignServicesIcon />}
        >
          선택 완료
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DesignSelectModal;
